"use client";

import {
  deletePortfolioRecord,
  getPortfolioRecordDocumentQuota,
  getPortfolioRecords,
  getNextPortfolioRecordOccurrence,
  savePortfolioRecord,
  updatePortfolioRecord,
  type PortfolioRecord,
  type PortfolioRecordCategory,
  type PortfolioRecordDocument,
  type PortfolioRecordDocumentQuota,
  type PortfolioRecordReminderUnit,
  type PortfolioRecordRepeatUnit,
} from "@/lib/portfolio-records";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { DatePicker } from "@/components/portfolio/DatePicker";
import { RecordsSection } from "@/components/portfolio/records/RecordsSection";
import { RecordAttachmentRow } from "@/components/portfolio/records/RecordAttachmentRow";
import { RecordScheduleControl } from "@/components/portfolio/records/RecordScheduleControl";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { DocumentPlusIcon } from "@heroicons/react/24/outline";
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILE_COUNT = 5;

type FormState = {
  title: string;
  category: PortfolioRecordCategory;
  note: string;
  importantDate: string;
  repeats: boolean;
  repeatInterval: number;
  repeatUnit: PortfolioRecordRepeatUnit;
  reminds: boolean;
  reminderAmount: number;
  reminderUnit: PortfolioRecordReminderUnit;
};

const INITIAL_FORM: FormState = {
  title: "",
  category: "OTHER",
  note: "",
  importantDate: "",
  repeats: false,
  repeatInterval: 1,
  repeatUnit: "YEAR",
  reminds: false,
  reminderAmount: 1,
  reminderUnit: "MONTH",
};

function createId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatFileSize(size: number) {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} kB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function titleFromFileName(fileName: string) {
  return fileName.replace(/\.[^/.]+$/, "").trim();
}

export function Records() {
  const { userInfoGlobal } = useAppContext();
  const { selectedEstate } = useEstateContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      eyebrow: "Dokumenty",
      description:
        "Mějte smlouvy, pojistky a jejich výročí pohromadě u nemovitosti.",
      add: "Přidat záznam",
      emptyTitle: "Zatím tu nic není",
      emptyDescription:
        "Přidejte například nájemní smlouvu, pojistku nebo datum revize.",
      documents: "Dokumenty",
      dates: "Termíny",
      title: "Název",
      titlePlaceholder: "Např. Pojištění nemovitosti",
      note: "Poznámka",
      importantDate: "Důležité datum / výročí",
      selectDate: "Vyberte datum",
      repeat: "Opakovat termín",
      repeatEvery: "Opakovat {every}",
      reminder: "Připomenout",
      reminderBefore: "Připomenout {count} předem",
      dayUnit: "den",
      daysUnit: "dny",
      files: "Dokumenty",
      uploadTitle: "Přetáhněte soubory nebo je vyberte",
      uploadHint: "PDF, obrázky nebo kancelářské dokumenty · max. 10 MB",
      chooseFiles: "Vybrat soubory",
      save: "Uložit",
      edit: "Upravit",
      update: "Uložit změny",
      cancel: "Zrušit",
      dialogTitle: "Nový záznam",
      editDialogTitle: "Upravit záznam",
      dialogDescription: "Vyplňte alespoň datum nebo přiložte dokument.",
      required: "Zadejte název a přidejte datum nebo dokument.",
      saved: "Záznam byl uložen.",
      updated: "Záznam byl upraven.",
      deleted: "Položka byla odstraněna.",
      loadError: "Záznamy se nepodařilo načíst.",
      saveError: "Položku se nepodařilo uložit.",
      deleteError: "Položku se nepodařilo odstranit.",
      invalidFile: "Každý soubor může mít nejvýše 10 MB.",
      tooManyFiles: "K jedné položce lze přidat nejvýše 5 souborů.",
      fileLimitReached:
        "Tato funkce je stále ve vývoji. Pokud jste dosáhli maximálního počtu 5 souborů, pošlete nám prosím požadavek, abychom její vývoj urychlili.",
      delete: "Odstranit",
      deleteTitle: "Odstranit záznam?",
      deleteDescription: "Položka i přiložené dokumenty budou odstraněny.",
      download: "Stáhnout",
      noDate: "Bez termínu",
      overdue: "Po termínu",
      today: "Dnes",
      inDays: "Za {count} dní",
      repeatsEvery: "Opakuje se {every} {count}",
      monthUnit: "měsíc",
      monthsUnit: "měsíce",
      yearUnit: "rok",
      yearsUnit: "roky",
      categories: {
        INSURANCE: "Pojištění",
        LEASE: "Nájemní smlouva",
        TAX: "Daně",
        INSPECTION: "Revize",
        WARRANTY: "Záruka",
        OTHER: "Ostatní",
      },
      locale: "cs-CZ",
    },
    en: {
      eyebrow: "Documents",
      description:
        "Keep agreements, insurance documents, and anniversaries together with the property.",
      add: "Add document or date",
      emptyTitle: "Nothing here yet",
      emptyDescription:
        "Add a lease agreement, insurance policy, or inspection date.",
      documents: "Documents",
      dates: "Dates",
      title: "Title",
      titlePlaceholder: "E.g. Property insurance",
      note: "Note",
      importantDate: "Important date / anniversary",
      selectDate: "Select a date",
      repeat: "Repeat date",
      repeatEvery: "Repeat {every}",
      reminder: "Reminder",
      reminderBefore: "Remind {count} before",
      dayUnit: "day",
      daysUnit: "days",
      files: "Documents",
      uploadTitle: "Drop files here or browse",
      uploadHint: "PDF, images, or office documents · max. 10 MB",
      chooseFiles: "Choose files",
      save: "Save",
      edit: "Edit",
      update: "Save changes",
      cancel: "Cancel",
      dialogTitle: "New document or important date",
      editDialogTitle: "Edit document or important date",
      dialogDescription: "Provide at least a date or an attachment.",
      required: "Enter a title and add a date or document.",
      saved: "The document or date was saved.",
      updated: "The document or date was updated.",
      deleted: "The item was removed.",
      loadError: "Records could not be loaded.",
      saveError: "The item could not be saved.",
      deleteError: "The item could not be removed.",
      invalidFile: "Each file can be up to 10 MB.",
      tooManyFiles: "You can attach up to 5 files to one item.",
      fileLimitReached:
        "This feature is still in development. If you have reached the maximum of 5 files, please send us a request to help speed up its development.",
      delete: "Delete",
      deleteTitle: "Delete this document or date?",
      deleteDescription: "The item and its attachments will be removed.",
      download: "Download",
      noDate: "No date",
      overdue: "Overdue",
      today: "Today",
      inDays: "In {count} days",
      repeatsEvery: "Repeats every {count}",
      monthUnit: "month",
      monthsUnit: "months",
      yearUnit: "year",
      yearsUnit: "years",
      categories: {
        INSURANCE: "Insurance",
        LEASE: "Lease agreement",
        TAX: "Taxes",
        INSPECTION: "Inspection",
        WARRANTY: "Warranty",
        OTHER: "Other",
      },
      locale: "en-GB",
    },
  }[language];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [records, setRecords] = useState<PortfolioRecord[]>([]);
  const [documentQuota, setDocumentQuota] =
    useState<PortfolioRecordDocumentQuota>({
      count: 0,
      maximum: MAX_FILE_COUNT,
      limitReached: false,
    });
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [files, setFiles] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<
    PortfolioRecordDocument[]
  >([]);
  const [editingRecord, setEditingRecord] = useState<PortfolioRecord | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;

    if (!selectedEstate || !userInfoGlobal?.token || !userInfoGlobal.email) {
      setRecords([]);
      setDocumentQuota({
        count: 0,
        maximum: MAX_FILE_COUNT,
        limitReached: false,
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const user = {
      email: userInfoGlobal.email,
      token: userInfoGlobal.token,
    };
    Promise.all([
      getPortfolioRecords(selectedEstate.id, user),
      getPortfolioRecordDocumentQuota(user),
    ])
      .then(([items, quota]) => {
        if (!cancelled) {
          setRecords(items);
          setDocumentQuota(quota);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(copy.loadError);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    copy.loadError,
    selectedEstate,
    userInfoGlobal?.email,
    userInfoGlobal?.token,
  ]);

  const today = useMemo(() => {
    const value = new Date();
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  }, []);
  const sortedRecords = useMemo(
    () =>
      [...records].sort((first, second) => {
        const firstDate =
          getNextPortfolioRecordOccurrence(first, today)?.getTime() ?? Infinity;
        const secondDate =
          getNextPortfolioRecordOccurrence(second, today)?.getTime() ??
          Infinity;
        return firstDate - secondDate;
      }),
    [records, today],
  );
  const dateCount = records.filter((record) => record.importantDate).length;
  const documentCount = records.reduce(
    (total, record) => total + record.documents.length,
    0,
  );
  const documentCountLabel =
    language === "cs"
      ? `${documentCount} ${
          documentCount === 1
            ? "dokument"
            : documentCount >= 2 && documentCount <= 4
              ? "dokumenty"
              : "dokumentů"
        }`
      : `${documentCount} ${documentCount === 1 ? "document" : "documents"}`;
  const dateCountLabel =
    language === "cs"
      ? `${dateCount} ${
          dateCount === 1
            ? "termín"
            : dateCount >= 2 && dateCount <= 4
              ? "termíny"
              : "termínů"
        }`
      : `${dateCount} ${dateCount === 1 ? "date" : "dates"}`;

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setFiles([]);
    setExistingDocuments([]);
    setEditingRecord(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (record: PortfolioRecord) => {
    setEditingRecord(record);
    setExistingDocuments(record.documents);
    setFiles([]);
    setForm({
      title: record.title,
      category: record.category,
      note: record.note,
      importantDate: record.importantDate ?? "",
      repeats: Boolean(record.repeatInterval && record.repeatUnit),
      repeatInterval: record.repeatInterval ?? 1,
      repeatUnit: record.repeatUnit ?? "YEAR",
      reminds: Boolean(record.reminderAmount && record.reminderUnit),
      reminderAmount: record.reminderAmount ?? 1,
      reminderUnit: record.reminderUnit ?? "MONTH",
    });
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsDialogOpen(true);
  };

  const addFiles = (incomingFiles: File[]) => {
    if (documentQuota.limitReached) {
      toast.error(copy.fileLimitReached);
      return false;
    }
    if (incomingFiles.some((file) => file.size > MAX_FILE_SIZE)) {
      toast.error(copy.invalidFile);
      return false;
    }

    let didAdd = false;
    setFiles((current) => {
      const uniqueFiles = incomingFiles.filter(
        (file) =>
          !current.some(
            (existing) =>
              existing.name === file.name &&
              existing.size === file.size &&
              existing.lastModified === file.lastModified,
          ),
      );
      if (
        existingDocuments.length + current.length + uniqueFiles.length >
        MAX_FILE_COUNT
      ) {
        toast.error(copy.tooManyFiles);
        return current;
      }
      const originalDocumentCount = editingRecord?.documents.length ?? 0;
      const prospectiveDocumentCount =
        documentQuota.count -
        originalDocumentCount +
        existingDocuments.length +
        current.length +
        uniqueFiles.length;
      if (prospectiveDocumentCount > documentQuota.maximum) {
        toast.error(copy.fileLimitReached);
        return current;
      }
      didAdd = uniqueFiles.length > 0;
      if (uniqueFiles[0]) {
        const title = titleFromFileName(uniqueFiles[0].name);
        if (title) {
          setForm((formState) => ({
            ...formState,
            title: formState.title.trim() ? formState.title : title,
          }));
        }
      }
      return [...current, ...uniqueFiles];
    });
    return didAdd;
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(event.dataTransfer.files));
  };

  const handleEmptyDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length) {
      addFiles(droppedFiles);
      setIsDialogOpen(true);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !selectedEstate ||
      !form.title.trim() ||
      (!form.importantDate && !files.length && !existingDocuments.length)
    ) {
      toast.error(copy.required);
      return;
    }

    const record: PortfolioRecord = {
      id: editingRecord?.id ?? createId(),
      estateId: selectedEstate.id,
      title: form.title.trim(),
      category: form.category,
      note: form.note.trim(),
      importantDate: form.importantDate || null,
      repeatInterval:
        form.importantDate && form.repeats
          ? Math.max(1, form.repeatInterval)
          : null,
      repeatUnit: form.importantDate && form.repeats ? form.repeatUnit : null,
      reminderAmount:
        form.importantDate && form.reminds
          ? Math.max(1, form.reminderAmount)
          : null,
      reminderUnit:
        form.importantDate && form.reminds ? form.reminderUnit : null,
      documents: [
        ...existingDocuments,
        ...files.map((file) => ({
          id: createId(),
          name: file.name,
          size: file.size,
          type: file.type,
          blob: file,
        })),
      ],
      createdAt: editingRecord?.createdAt ?? new Date().toISOString(),
    };

    const isEditing = Boolean(editingRecord);
    const previousDocumentCount = editingRecord?.documents.length ?? 0;

    setIsSaving(true);
    setIsDialogOpen(false);
    resetForm();
    try {
      if (!userInfoGlobal?.token || !userInfoGlobal.email) {
        throw new Error("Missing user");
      }
      const user = {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
      };
      const savedRecord = isEditing
        ? await updatePortfolioRecord(record, user)
        : await savePortfolioRecord(record, user);
      setRecords((current) =>
        isEditing
          ? current.map((item) =>
              item.id === savedRecord.id ? savedRecord : item,
            )
          : [...current, savedRecord],
      );
      setDocumentQuota((current) => {
        const count =
          current.count - previousDocumentCount + savedRecord.documents.length;
        return {
          ...current,
          count,
          limitReached: count >= current.maximum,
        };
      });
      toast.success(isEditing ? copy.updated : copy.saved);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : copy.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (record: PortfolioRecord) => {
    try {
      if (!userInfoGlobal?.token || !userInfoGlobal.email) {
        throw new Error("Missing user");
      }
      await deletePortfolioRecord(record, {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
      });
      setRecords((current) => current.filter((item) => item.id !== record.id));
      setDocumentQuota((current) => {
        const count = Math.max(0, current.count - record.documents.length);
        return {
          ...current,
          count,
          limitReached: count >= current.maximum,
        };
      });
      toast.success(copy.deleted);
    } catch {
      toast.error(copy.deleteError);
    }
  };

  const handleDownload = (document: PortfolioRecord["documents"][number]) => {
    const url = URL.createObjectURL(document.blob);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = document.name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const getDateMeta = (record: PortfolioRecord) => {
    const nextOccurrence = getNextPortfolioRecordOccurrence(record, today);
    if (!nextOccurrence || !record.importantDate) {
      return { label: copy.noDate, status: "neutral" as const };
    }

    const baseDate = new Date(`${record.importantDate}T12:00:00`);
    const displayedDate =
      record.repeatInterval && record.repeatUnit ? nextOccurrence : baseDate;
    const days = Math.round(
      (displayedDate.getTime() - today.getTime()) / 86_400_000,
    );
    const dateLabel = new Intl.DateTimeFormat(copy.locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(displayedDate);
    const statusLabel =
      days < 0
        ? copy.overdue
        : days === 0
          ? copy.today
          : days <= 60
            ? copy.inDays.replace("{count}", String(days))
            : null;

    return {
      label: dateLabel,
      statusLabel,
      status:
        days < 0
          ? ("danger" as const)
          : days <= 60
            ? ("soon" as const)
            : ("neutral" as const),
    };
  };

  const repeatLabel = (record: PortfolioRecord) => {
    if (!record.repeatInterval || !record.repeatUnit) return null;
    return copy.repeatsEvery
      .replace("{every}", formatRepeatEvery(record.repeatInterval))
      .replace(
        "{count}",
        `${record.repeatInterval} ${formatRepeatUnit(
          record.repeatInterval,
          record.repeatUnit,
        )}`,
      );
  };

  const reminderLabel = (record: PortfolioRecord) => {
    if (!record.reminderAmount || !record.reminderUnit) return null;
    return copy.reminderBefore.replace(
      "{count}",
      `${record.reminderAmount} ${formatReminderUnit(
        record.reminderAmount,
        record.reminderUnit,
      )}`,
    );
  };

  const repeatEveryLabel = (count: number) =>
    copy.repeatEvery.replace("{every}", formatRepeatEvery(count));

  const formatRepeatEvery = (count: number) => {
    if (language !== "cs") return "every";
    return count >= 1 && count <= 4 ? "každý" : "každých";
  };

  const formatRepeatUnit = (count: number, unit: PortfolioRecordRepeatUnit) => {
    if (language !== "cs") {
      if (unit === "YEAR") return count === 1 ? copy.yearUnit : copy.yearsUnit;
      return count === 1 ? copy.monthUnit : copy.monthsUnit;
    }

    if (unit === "YEAR") {
      if (count === 1) return "rok";
      if (count >= 2 && count <= 4) return "roky";
      return "let";
    }

    if (count === 1) return "měsíc";
    if (count >= 2 && count <= 4) return "měsíce";
    return "měsíců";
  };

  const formatReminderUnit = (
    count: number,
    unit: PortfolioRecordReminderUnit,
  ) => {
    if (language !== "cs") {
      if (unit === "DAY") return count === 1 ? copy.dayUnit : copy.daysUnit;
      return count === 1 ? copy.monthUnit : copy.monthsUnit;
    }

    if (unit === "DAY") {
      if (count === 1) return "den";
      if (count >= 2 && count <= 4) return "dny";
      return "dnů";
    }

    if (count === 1) return "měsíc";
    if (count >= 2 && count <= 4) return "měsíce";
    return "měsíců";
  };

  if (!selectedEstate) return null;

  return (
    <>
      <RecordsSection
        records={sortedRecords}
        isLoading={isLoading}
        isDragging={isDragging}
        copy={copy}
        documentCountLabel={documentCountLabel}
        dateCountLabel={dateCountLabel}
        getDateMeta={getDateMeta}
        getRecurrenceLabel={repeatLabel}
        getReminderLabel={reminderLabel}
        formatFileSize={formatFileSize}
        onCreate={openCreateDialog}
        onEdit={openEditDialog}
        onDelete={(record) => void handleDelete(record)}
        onDownload={handleDownload}
        onDraggingChange={setIsDragging}
        onEmptyDrop={handleEmptyDrop}
      />

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open && !isSaving) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="app-dialog-title">
              {editingRecord ? copy.editDialogTitle : copy.dialogTitle}
            </DialogTitle>
            <DialogDescription className="app-dialog-description">
              {copy.dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="portfolio-record-title">{copy.title}</Label>
                <Input
                  id="portfolio-record-title"
                  value={form.title}
                  placeholder={copy.titlePlaceholder}
                  className="mb-0 h-10"
                  autoFocus
                  required
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2 [&>div]:mb-0 [&_button]:h-10">
                <Label htmlFor="portfolio-record-date">
                  {copy.importantDate}
                </Label>
                <DatePicker
                  id="portfolio-record-date"
                  label={copy.importantDate}
                  placeholder={copy.selectDate}
                  disablePortal
                  value={
                    form.importantDate
                      ? new Date(`${form.importantDate}T12:00:00`).toISOString()
                      : null
                  }
                  useDays
                  onChange={(value) => {
                    const nextDate = value
                      ? new Date(value).toISOString().slice(0, 10)
                      : "";
                    setForm((current) => ({
                      ...current,
                      importantDate: nextDate,
                      repeats: nextDate ? current.repeats : false,
                      reminds: nextDate ? current.reminds : false,
                    }));
                  }}
                />
              </div>

              <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
                <RecordScheduleControl
                  id="portfolio-record-repeat"
                  label={copy.repeat}
                  enabled={form.repeats}
                  disabled={!form.importantDate}
                  amount={form.repeatInterval}
                  amountLabel={repeatEveryLabel(form.repeatInterval)}
                  selectedValue={form.repeatUnit}
                  options={[
                    {
                      value: "MONTH",
                      label: formatRepeatUnit(form.repeatInterval, "MONTH"),
                    },
                    {
                      value: "YEAR",
                      label: formatRepeatUnit(form.repeatInterval, "YEAR"),
                    },
                  ]}
                  onEnabledChange={(repeats) =>
                    setForm((current) => ({ ...current, repeats }))
                  }
                  onAmountChange={(repeatInterval) =>
                    setForm((current) => ({ ...current, repeatInterval }))
                  }
                  onValueChange={(repeatUnit) =>
                    setForm((current) => ({ ...current, repeatUnit }))
                  }
                />

                <RecordScheduleControl
                  id="portfolio-record-reminder"
                  label={copy.reminder}
                  enabled={form.reminds}
                  disabled={!form.importantDate}
                  amount={form.reminderAmount}
                  amountLabel={copy.reminder}
                  selectedValue={form.reminderUnit}
                  options={[
                    {
                      value: "DAY",
                      label: formatReminderUnit(form.reminderAmount, "DAY"),
                    },
                    {
                      value: "MONTH",
                      label: formatReminderUnit(form.reminderAmount, "MONTH"),
                    },
                  ]}
                  onEnabledChange={(reminds) =>
                    setForm((current) => ({ ...current, reminds }))
                  }
                  onAmountChange={(reminderAmount) =>
                    setForm((current) => ({ ...current, reminderAmount }))
                  }
                  onValueChange={(reminderUnit) =>
                    setForm((current) => ({ ...current, reminderUnit }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{copy.files}</Label>
              {documentQuota.limitReached ? (
                <Alert tone="warning">{copy.fileLimitReached}</Alert>
              ) : null}
              <div
                className={`rounded-md border border-dashed p-5 text-center transition-colors ${
                  documentQuota.limitReached
                    ? "cursor-not-allowed opacity-60"
                    : isDragging
                      ? "border-softiq-secondary bg-softiq-secondary-shade-100"
                      : "border-softiq-primary/20 bg-softiq-primary/[0.02] dark:border-white/15 dark:bg-white/[0.02]"
                }`}
                aria-disabled={documentQuota.limitReached}
                onDragEnter={(event) => {
                  event.preventDefault();
                  if (!documentQuota.limitReached) setIsDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                  if (
                    !event.currentTarget.contains(event.relatedTarget as Node)
                  )
                    setIsDragging(false);
                }}
                onDrop={handleDrop}
              >
                <DocumentPlusIcon
                  aria-hidden="true"
                  className="mx-auto size-7 text-softiq-primary/45 dark:text-white/45"
                />
                <p className="mt-2 text-sm font-medium">{copy.uploadTitle}</p>
                <p className="app-section-description mt-1">
                  {copy.uploadHint}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  multiple
                  disabled={documentQuota.limitReached}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={documentQuota.limitReached}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {copy.chooseFiles}
                </Button>
              </div>

              {existingDocuments.length || files.length ? (
                <div className="space-y-1.5">
                  {existingDocuments.map((document) => (
                    <RecordAttachmentRow
                      key={document.id}
                      name={document.name}
                      size={document.size}
                      deleteLabel={copy.delete}
                      formatFileSize={formatFileSize}
                      onDelete={() =>
                        setExistingDocuments((current) =>
                          current.filter((item) => item.id !== document.id),
                        )
                      }
                    />
                  ))}
                  {files.map((file, index) => (
                    <RecordAttachmentRow
                      key={`${file.name}-${file.lastModified}`}
                      name={file.name}
                      size={file.size}
                      deleteLabel={copy.delete}
                      formatFileSize={formatFileSize}
                      onDelete={() =>
                        setFiles((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    />
                  ))}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolio-record-note">{copy.note}</Label>
              <Textarea
                id="portfolio-record-note"
                value={form.note}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    note: event.target.value,
                  }))
                }
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="form"
                onClick={() => setIsDialogOpen(false)}
              >
                {copy.cancel}
              </Button>
              <Button
                type="submit"
                size="form"
                loading={isSaving}
                loadingLabel={editingRecord ? copy.update : copy.save}
              >
                {editingRecord ? copy.update : copy.save}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
